import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';

const STORAGE_KEY = 'rabbithole_v1';
const ARCHIVE_KEY = 'rabbithole_archive_v1';
function loadState() { try { const r = localStorage.getItem(STORAGE_KEY); if (r) return JSON.parse(r); } catch {} return null; }
function saveState(h) { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(h)); } catch {} }
function loadArchive() { try { const r = localStorage.getItem(ARCHIVE_KEY); if (r) return JSON.parse(r); } catch {} return []; }
function saveArchive(a) { try { localStorage.setItem(ARCHIVE_KEY, JSON.stringify(a)); } catch {} }
function uid() { return Math.random().toString(36).slice(2, 10); }

function parseBody(raw) {
  const parts = []; const re = /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g;
  let last = 0, m;
  while ((m = re.exec(raw)) !== null) {
    if (m.index > last) parts.push({ type: 'text', value: raw.slice(last, m.index) });
    parts.push({ type: 'link', topic: m[1].trim(), label: (m[2] || m[1]).trim() });
    last = re.lastIndex;
  }
  if (last < raw.length) parts.push({ type: 'text', value: raw.slice(last) });
  return parts;
}

function newHole(id) {
  return { id: id || uid(), title: 'New Rabbit Hole', burrows: [], currentId: null, createdAt: Date.now() };
}

const RANDOM_TOPICS = [
  'How do black holes form', 'What caused the fall of Rome', 'How does memory work',
  'What is dark matter', 'How did language evolve', 'What is consciousness',
  'How do vaccines work', 'What is the Fermi paradox', 'How does the internet work',
  'What caused World War I', 'How do dreams work', 'What is quantum entanglement',
  'How did life begin', 'How does evolution work', 'What is the gut microbiome',
  'How do neurons fire', 'What is inflation', 'How was the universe created',
  'What is CRISPR', 'How does DNA work', 'What is the stock market',
  'How does the brain store memories', 'What is string theory', 'How do planes fly',
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}

function getSuggestions(allBurrows) {
  const recent = allBurrows.slice(-10).map(b => b.topic.toLowerCase());
  return shuffle(RANDOM_TOPICS).filter(t => !recent.includes(t.toLowerCase())).slice(0, 8);
}

// Build the ancestor trail for a given burrow (for context)
function buildTrail(burrows, currentId) {
  const byId = {};
  burrows.forEach(b => { byId[b.id] = b; });
  const trail = [];
  let cur = byId[currentId];
  while (cur) {
    trail.unshift({ topic: cur.topic, body: cur.body });
    cur = cur.parentId ? byId[cur.parentId] : null;
  }
  return trail;
}

// ── Streaming fetch ───────────────────────────────────────────────────────────
async function streamAsk(topic, trail, onChunk, onDone, onError) {
  try {
    const res = await fetch('https://rabbithole-api.onrender.com/api/ask', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ topic, trail }),
    });

    if (!res.ok) { onError('Server error'); return; }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullText = '';
    let sources = [];

    let finished = false;
    const processChunks = async () => {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          if (!finished) { finished = true; onDone(fullText, sources); }
          return;
        }
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const raw = line.slice(6).trim();
            if (raw === '[DONE]') {
              if (!finished) { finished = true; onDone(fullText, sources); }
              return;
            }
            try {
              const parsed = JSON.parse(raw);
              if (parsed.error) { onError(parsed.error); return; }
              if (parsed.sources) { sources = parsed.sources; }
              if (parsed.text) {
                fullText += parsed.text;
                onChunk(fullText);
              }
            } catch {}
          }
        }
      }
    };

    await processChunks();
  } catch (e) {
    onError(e.message);
  }
}

async function apiFetch(path, body) {
  const res = await fetch('https://rabbithole-api.onrender.com' + path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error');
  return data;
}

// ── Warren Manager ───────────────────────────────────────────────────────────
// ── Menu Panel ───────────────────────────────────────────────────────────────
function MenuPanel({ holes, activeHole, archive, onSwitch, onCloseHole, onRestore, onDeletePermanent, onViewWarren, onClose, onRename, onNewHole }) {
  const [tab, setTab] = useState('open');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [hoveredId, setHoveredId] = useState(null);
  const otherHoles = holes.filter(h => h.id !== activeHole.id);
  return (
    <div style={MP.overlay} onClick={onClose}>
      <div style={MP.sheet} onClick={e => e.stopPropagation()}>
        <div style={MP.header}>
          <div style={MP.title}>Menu</div>
          <button style={MP.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={MP.tabs}>
          <button style={{ ...MP.tabBtn, ...(tab === 'open' ? MP.tabActive : {}) }} onClick={() => setTab('open')}
            onMouseEnter={e => { if (tab !== 'open') { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = '#ffffff'; }}}
            onMouseLeave={e => { if (tab !== 'open') { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888888'; }}}>
            Your Rabbit Holes <span style={MP.badge}>{holes.length}</span>
          </button>
          <button style={{ ...MP.tabBtn, ...(tab === 'archived' ? MP.tabActive : {}) }} onClick={() => setTab('archived')}
            onMouseEnter={e => { if (tab !== 'archived') { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = '#ffffff'; }}}
            onMouseLeave={e => { if (tab !== 'archived') { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888888'; }}}>
            Archived <span style={MP.badge}>{archive.length}</span>
          </button>
        </div>
        {tab === 'open' && (
          <div style={MP.list}>
            {holes.length === 0 ? <div style={MP.empty}>No rabbit holes yet.</div>
            : holes.map(h => (
              <div key={h.id} style={{ ...MP.item, cursor: 'pointer', borderColor: h.id === activeHole.id ? '#00ff88' : '#2a2a2a' }}
                onClick={() => { if (editingId !== h.id) onSwitch(h.id); }}
                onContextMenu={e => { e.preventDefault(); setEditingId(h.id); setEditValue(h.title); }}
                onTouchStart={e => {
                  const t = setTimeout(() => { setEditingId(h.id); setEditValue(h.title); }, 600);
                  e.currentTarget._pressTimer = t;
                }}
                onTouchEnd={e => { clearTimeout(e.currentTarget._pressTimer); }}
                onTouchMove={e => { clearTimeout(e.currentTarget._pressTimer); }}
                onMouseEnter={e => { if (editingId !== h.id) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)'; setHoveredId(h.id); }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = h.id === activeHole.id ? '#00ff88' : '#2a2a2a'; setHoveredId(null); }}
              >
                <div style={MP.itemLeft}>
                  {editingId === h.id ? (
                    <input
                      autoFocus
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      onBlur={() => { if (editValue.trim()) onRename(h.id, editValue.trim()); setEditingId(null); }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { if (editValue.trim()) onRename(h.id, editValue.trim()); setEditingId(null); }
                        if (e.key === 'Escape') setEditingId(null);
                        e.stopPropagation();
                      }}
                      style={{ fontSize: 14, fontWeight: 500, background: 'transparent', border: 'none', borderBottom: '1px solid #00b359', color: '#ffffff', outline: 'none', width: '100%', padding: '2px 0', fontFamily: 'Inter, sans-serif' }}
                    />
                  ) : (
                    <div style={MP.itemTitle}>{h.title}</div>
                  )}
                  <div style={MP.itemMeta}>{h.burrows.length} {h.burrows.length === 1 ? 'burrow' : 'burrows'}</div>
                  <div style={{ ...MP.itemBurrows, transition: 'all 0.2s' }}>
                    {h.burrows.slice(0, hoveredId === h.id ? 10 : 3).map(b => <span key={b.id} style={MP.burrowPill}>{b.label || b.topic}</span>)}
                    {h.burrows.length > (hoveredId === h.id ? 10 : 3) && <span style={MP.burrowMore}>+{h.burrows.length - (hoveredId === h.id ? 10 : 3)} more</span>}
                  </div>
                </div>
                <span style={{ fontSize: 14, color: '#444444', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, border: '1px solid transparent', flexShrink: 0 }}
                  onClick={e => { e.stopPropagation(); onCloseHole(h.id); }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#444444'; e.currentTarget.style.borderColor = 'transparent'; }}
                >✕</span>
              </div>
            ))}
          </div>
        )}
        {tab === 'archived' && (
          <div style={MP.list}>
            {archive.length === 0 ? <div style={MP.empty}>No archived holes yet.</div>
            : [...archive].reverse().map(h => (
              <div key={h.id} style={MP.item}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; setHoveredId(h.id); }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; setHoveredId(null); }}
              >
                <div style={MP.itemLeft}>
                  <div style={MP.itemTitle}>{h.title}</div>
                  <div style={MP.itemMeta}>{h.burrows.length} {h.burrows.length === 1 ? 'burrow' : 'burrows'}{h.deletedAt && <span> · deleted {new Date(h.deletedAt).toLocaleDateString()}</span>}</div>
                  <div style={{ ...MP.itemBurrows, transition: 'all 0.2s' }}>
                    {h.burrows.slice(0, hoveredId === h.id ? 10 : 3).map(b => <span key={b.id} style={MP.burrowPill}>{b.label || b.topic}</span>)}
                    {h.burrows.length > (hoveredId === h.id ? 10 : 3) && <span style={MP.burrowMore}>+{h.burrows.length - (hoveredId === h.id ? 10 : 3)} more</span>}
                  </div>
                </div>
                <div style={MP.itemActions}>
                  <button style={MP.restoreBtn} onClick={() => onRestore(h)}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#00ff88'; e.currentTarget.style.color = '#00ff88'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888888'; }}
                  >Restore</button>
                  <button style={MP.deleteBtn} onClick={() => onDeletePermanent(h.id)}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#ff3333'; e.currentTarget.style.color = '#ff3333'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#555555'; }}
                  >Delete forever</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {tab === 'open' && <div style={MP.newHoleBar}>
          <button style={MP.newHoleFullBtn} onClick={onNewHole}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = '#ffffff'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888888'; }}
          >+ New Rabbit Hole</button>
        </div>}
      </div>
    </div>
  );
}
const MP = {
  overlay: { position: 'fixed', inset: 0, zIndex: 210, background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', padding: '54px 20px 0', animation: 'fadeIn 0.2s ease' },
  sheet: { width: '95vw', maxWidth: 400, background: '#1a1a1a', borderRadius: 16, border: '1px solid #2a2a2a', boxShadow: '0 8px 40px rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', maxHeight: '80vh', animation: 'grow 0.25s cubic-bezier(0.4,0,0.2,1)' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' },
  title: { fontSize: 20, fontWeight: 600, color: '#ffffff', fontFamily: "'Playfair Display', serif" },
  closeBtn: { fontSize: 18, background: 'none', border: 'none', color: '#555555', cursor: 'pointer', padding: '0 4px' },
  newBtn: { fontSize: 13, padding: '6px 14px', borderRadius: 10, border: '1px solid #2a2a2a', background: 'transparent', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', transition: 'all 0.15s' },
  newHoleBar: { padding: '12px 16px 16px', borderTop: '1px solid #2a2a2a', flexShrink: 0 },
  newHoleFullBtn: { width: '100%', fontSize: 14, padding: '12px', borderRadius: 10, border: '1px solid #2a2a2a', background: 'transparent', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s' },
  tabs: { display: 'flex', gap: 6, padding: '14px 20px 0' },
  tabBtn: { fontSize: 12, padding: '5px 12px', borderRadius: 20, border: '1px solid #2a2a2a', background: 'transparent', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s' },
  tabActive: { background: 'transparent', color: '#ffffff', borderColor: '#00b359' },
  badge: { fontSize: 10, background: '#2a2a2a', color: '#666666', padding: '1px 5px', borderRadius: 8 },
  list: { overflowY: 'auto', padding: '10px 16px 10px', flex: 1, minHeight: 0 },
  item: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '12px', borderRadius: 10, border: '1px solid #2a2a2a', marginBottom: 8, gap: 12, transition: 'border-color 0.15s' },
  itemLeft: { flex: 1, minWidth: 0 },
  itemTitle: { fontSize: 14, fontWeight: 500, color: '#ffffff', marginBottom: 3 },
  itemMeta: { fontSize: 11, color: '#555555', marginBottom: 6 },
  itemBurrows: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  burrowPill: { fontSize: 10, color: '#00b359', background: 'rgba(0,179,89,0.08)', border: '1px solid rgba(0,179,89,0.15)', padding: '1px 6px', borderRadius: 10 },
  burrowMore: { fontSize: 10, color: '#444444', padding: '1px 6px' },
  itemActions: { display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 },
  restoreBtn: { fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #2a2a2a', background: 'transparent', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s', whiteSpace: 'nowrap' },
  deleteBtn: { fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #2a2a2a', background: 'transparent', color: '#555555', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s', whiteSpace: 'nowrap' },
  empty: { display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#444444', fontStyle: 'italic', padding: '32px 20px', textAlign: 'center' },
};


// ── Combined Warren Panel ────────────────────────────────────────────────────
function WarrenPanel({ activeHole, holes, archive, onJump, onSwitch, onRestore, onDeletePermanent, onClose, onViewWarren, onRenameHole, onCloseHole }) {
  const [tab, setTab] = useState('current');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [hoveredId, setHoveredId] = useState(null);
  const { burrows, currentId } = activeHole;
  const dragRef = useRef(null);
  const mapRef = useRef(null);
  const [transform, setTransform] = useState({ x: 40, y: 40, scale: 1 });
  const total = burrows.length;

  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    const handler = e => {
      e.preventDefault();
      e.stopPropagation();
      if (e.ctrlKey || e.metaKey) {
        const rect = el.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        setTransform(t => {
          const newScale = Math.min(2.5, Math.max(0.3, t.scale - e.deltaY * 0.01));
          const ratio = newScale / t.scale;
          return { scale: newScale, x: cx - ratio * (cx - t.x), y: cy - ratio * (cy - t.y) };
        });
      } else {
        setTransform(t => ({ ...t, x: t.x - e.deltaX, y: t.y - e.deltaY }));
      }
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [mapRef.current]);

  // Build tree for map
  const byId = {};
  burrows.forEach(b => { byId[b.id] = { ...b, children: [] }; });
  const roots = [];
  burrows.forEach(b => {
    if (b.parentId && byId[b.parentId]) byId[b.parentId].children.push(byId[b.id]);
    else roots.push(byId[b.id]);
  });

  const NODE_W = 164, NODE_H = 76, H_GAP = 70, V_GAP = 20;
  const positions = {};
  let maxX = 0, maxY = 0, globalSlot = 0;

  function layout(node, depth) {
    if (node.children.length === 0) {
      positions[node.id] = { x: depth * (NODE_W + H_GAP), y: globalSlot * (NODE_H + V_GAP) };
      globalSlot++;
    } else {
      node.children.forEach(child => layout(child, depth + 1));
      const firstY = positions[node.children[0].id].y;
      const lastY = positions[node.children[node.children.length - 1].id].y;
      positions[node.id] = { x: depth * (NODE_W + H_GAP), y: (firstY + lastY) / 2 };
    }
    const p = positions[node.id];
    if (p.x + NODE_W > maxX) maxX = p.x + NODE_W;
    if (p.y + NODE_H > maxY) maxY = p.y + NODE_H;
  }
  roots.forEach(r => layout(r, 0));

  const pathIds = new Set();
  let cur = byId[currentId];
  while (cur) { pathIds.add(cur.id); cur = cur.parentId ? byId[cur.parentId] : null; }

  const edges = burrows
    .filter(b => b.parentId && positions[b.parentId] && positions[b.id])
    .map(b => {
      const p = positions[b.parentId], c = positions[b.id];
      const onPath = pathIds.has(b.id) && pathIds.has(b.parentId);
      return { x1: p.x + NODE_W, y1: p.y + NODE_H / 2, x2: c.x, y2: c.y + NODE_H / 2, onPath };
    });

  const W = Math.max(maxX + 80, 500);
  const H = Math.max(maxY + 80, 300);

  const onMouseDown = e => { dragRef.current = { sx: e.clientX, sy: e.clientY, tx: transform.x, ty: transform.y }; };
  const onMouseMove = e => { if (!dragRef.current) return; const { tx, ty, sx, sy } = dragRef.current; const dx = e.clientX - sx; const dy = e.clientY - sy; setTransform(t => ({ ...t, x: tx + dx, y: ty + dy })); };
  const onMouseUp = () => { dragRef.current = null; };
  const onWheel = e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.ctrlKey || e.metaKey) {
      // Pinch zoom — zoom relative to center of the map element
      const rect = e.currentTarget.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      setTransform(t => {
        const newScale = Math.min(2.5, Math.max(0.3, t.scale - e.deltaY * 0.01));
        const ratio = newScale / t.scale;
        return {
          scale: newScale,
          x: cx - ratio * (cx - t.x),
          y: cy - ratio * (cy - t.y),
        };
      });
    } else {
      // Two-finger pan
      setTransform(t => ({ ...t, x: t.x - e.deltaX, y: t.y - e.deltaY }));
    }
  };

  const otherHoles = holes.filter(h => h.id !== activeHole.id);

  return (
    <div style={WP.overlay} onClick={onClose}>
      <div style={WP.sheet} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={WP.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
            {editingId === activeHole.id ? (
              <input
                autoFocus
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onBlur={() => { onRenameHole(activeHole.id, editValue); setEditingId(null); }}
                onKeyDown={e => { if (e.key === 'Enter') { onRenameHole(activeHole.id, editValue); setEditingId(null); } if (e.key === 'Escape') setEditingId(null); }}
                style={{ fontSize: 20, fontWeight: 600, fontFamily: "'Playfair Display', serif", background: 'transparent', border: 'none', borderBottom: '1px solid #00b359', color: '#ffffff', outline: 'none', width: '100%', padding: '2px 0' }}
              />
            ) : (
              <div style={{ ...WP.headerTitle, cursor: 'pointer', userSelect: 'none' }}
                onContextMenu={e => { e.preventDefault(); setEditingId(activeHole.id); setEditValue(activeHole.title); }}
                onTouchStart={e => {
                  const t = setTimeout(() => { setEditingId(activeHole.id); setEditValue(activeHole.title); }, 600);
                  e.currentTarget._pressTimer = t;
                }}
                onTouchEnd={e => { clearTimeout(e.currentTarget._pressTimer); }}
                onTouchMove={e => { clearTimeout(e.currentTarget._pressTimer); }}
              >{activeHole.title} — Map</div>
            )}
          </div>
          <button style={WP.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={WP.mapSubtitle}>{total} {total === 1 ? 'burrow' : 'burrows'}</div>

        {/* Current hole map */}
        {tab === 'current' && (
          <>
            {total > 0 && (
              <div style={WP.legend}>
                <div style={WP.legendItem}><div style={{ width: 10, height: 10, borderRadius: '50%', background: '#00ff88', flexShrink: 0 }} /><span>Current burrow</span></div>
                <div style={WP.legendItem}><div style={{ width: 20, height: 2, background: 'rgba(0,255,136,0.5)', borderRadius: 1, flexShrink: 0 }} /><span>Your trail</span></div>
                <div style={WP.legendItem}><div style={{ width: 10, height: 10, borderRadius: '50%', background: '#2a2a2a', border: '1px solid #444', flexShrink: 0 }} /><span>Visited burrow</span></div>
              </div>
            )}
            {total === 0 ? (
              <div style={WP.empty}>Start digging to see your warren grow.</div>
            ) : (
              <div ref={mapRef} style={WP.mapWrap} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} onWheel={onWheel} onTouchStart={e => {
                  e.preventDefault();
                  if (e.touches.length === 1) {
                    dragRef.current = { sx: e.touches[0].clientX, sy: e.touches[0].clientY, tx: transform.x, ty: transform.y, dist: null };
                  } else if (e.touches.length === 2) {
                    const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
                    dragRef.current = { sx: (e.touches[0].clientX + e.touches[1].clientX)/2, sy: (e.touches[0].clientY + e.touches[1].clientY)/2, tx: transform.x, ty: transform.y, dist, scale: transform.scale };
                  }
                }}
                onTouchMove={e => {
                  e.preventDefault();
                  if (!dragRef.current) return;
                  const dr = dragRef.current;
                  if (e.touches.length === 1 && dr.dist === null) {
                    const dx = e.touches[0].clientX - dr.sx;
                    const dy = e.touches[0].clientY - dr.sy;
                    const tx = dr.tx, ty = dr.ty;
                    setTransform(t => ({ ...t, x: tx + dx, y: ty + dy }));
                  } else if (e.touches.length === 2 && dr.dist !== null) {
                    const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
                    const newScale = Math.min(2.5, Math.max(0.3, dr.scale * (dist / dr.dist)));
                    const ratio = newScale / dr.scale;
                    const mx = (e.touches[0].clientX + e.touches[1].clientX)/2;
                    const my = (e.touches[0].clientY + e.touches[1].clientY)/2;
                    const { tx, ty, sx, sy } = dr;
                    setTransform({ scale: newScale, x: mx - ratio * (sx - tx), y: my - ratio * (sy - ty) });
                  }
                }}
                onTouchEnd={() => { dragRef.current = null; }}>
                <svg width={W} height={H} style={{ transform: `translate(${transform.x}px,${transform.y}px) scale(${transform.scale})`, transformOrigin: '0 0', cursor: 'grab', display: 'block', overflow: 'visible' }}>
                  <defs>
                    <marker id="arrowOn" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto"><path d="M0,0 L0,6 L7,3 z" fill="rgba(0,255,136,0.5)" /></marker>
                    <marker id="arrowOff" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto"><path d="M0,0 L0,6 L7,3 z" fill="#333" /></marker>
                  </defs>
                  {edges.map((e, i) => (
                    <path key={i} d={`M${e.x1},${e.y1} C${e.x1+40},${e.y1} ${e.x2-40},${e.y2} ${e.x2},${e.y2}`} fill="none" stroke={e.onPath ? 'rgba(0,255,136,0.45)' : '#2a2a2a'} strokeWidth={e.onPath ? 2 : 1} markerEnd={e.onPath ? 'url(#arrowOn)' : 'url(#arrowOff)'} />
                  ))}
                  {burrows.map(b => {
                    const pos = positions[b.id];
                    if (!pos) return null;
                    const isCurrent = b.id === currentId;
                    const isOnPath = pathIds.has(b.id);
                    return (
                      <g key={b.id} style={{ cursor: 'pointer' }} onClick={() => { onJump(b.id); }}
                        onMouseEnter={e => { const r = e.currentTarget.querySelector('.sel-ring'); if (r) r.setAttribute('stroke', 'rgba(255,255,255,0.5)'); }}
                        onMouseLeave={e => { const r = e.currentTarget.querySelector('.sel-ring'); if (r) r.setAttribute('stroke', 'transparent'); }}
                      >
                        {isCurrent && <rect x={pos.x-4} y={pos.y-4} width={NODE_W+8} height={NODE_H+8} rx={14} fill="none" stroke="rgba(0,255,136,0.15)" strokeWidth={8} />}
                        <rect className="sel-ring" x={pos.x-3} y={pos.y-3} width={NODE_W+6} height={NODE_H+6} rx={13} fill="none" stroke="transparent" strokeWidth={1.5} />
                        <rect x={pos.x} y={pos.y} width={NODE_W} height={NODE_H} rx={10} fill={isCurrent ? '#222222' : '#1a1a1a'} stroke={isCurrent ? '#00ff88' : isOnPath ? 'rgba(0,255,136,0.25)' : '#333333'} strokeWidth={isCurrent ? 1.5 : 1} />
                        <text x={pos.x+12} y={pos.y+44} fill='#ffffff' fontSize={13} fontWeight={500} fontFamily="Inter,sans-serif">{(b.label || b.topic).split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ').slice(0, 20)}</text>
                        {isCurrent && <circle cx={pos.x+NODE_W-16} cy={pos.y+16} r={5} fill="#00ff88" />}
                      </g>
                    );
                  })}
                </svg>
                <div style={WP.hint}>Scroll to zoom · Drag to pan · Click any burrow to jump back</div>
              </div>
            )}
          </>
        )}



      </div>
    </div>
  );
}

const WP = {
  overlay: { position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 0.2s ease' },
  sheet: { width: '82%', maxWidth: 740, background: '#1a1a1a', borderRadius: 16, border: '1px solid #333333', boxShadow: '0 8px 40px rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', height: '68vh', animation: 'grow 0.25s cubic-bezier(0.4,0,0.2,1)' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '22px 28px 0' },
  headerTitle: { fontSize: 24, fontWeight: 600, color: '#ffffff', fontFamily: "'Playfair Display', serif", letterSpacing: '-0.5px' },
  closeBtn: { fontSize: 20, background: 'none', border: 'none', color: '#555555', cursor: 'pointer', padding: '0 4px' },
  tabs: { display: 'flex', gap: 4, padding: '16px 28px 0' },
  tabBtn: { fontSize: 13, padding: '7px 16px', borderRadius: 20, border: '1px solid #2a2a2a', background: 'transparent', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', gap: 7, transition: 'all 0.15s' },
  tabActive: { background: 'transparent', color: '#ffffff', borderColor: '#00b359' },
  tabBadge: { fontSize: 10, background: '#2a2a2a', color: '#666666', padding: '1px 6px', borderRadius: 10 },
  tabDivider: { width: 1, height: 20, background: '#2a2a2a', marginLeft: 8, marginRight: 8, flexShrink: 0 },
  menuLabel: { fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: '#444444', marginRight: 6, whiteSpace: 'nowrap', alignSelf: 'center' },
  legend: { display: 'flex', gap: 20, padding: '14px 28px 0', flexWrap: 'wrap' },
  legendItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#555555' },
  mapWrap: { flex: 1, overflow: 'hidden', position: 'relative', marginTop: 14, background: '#111111', borderTop: '1px solid #2a2a2a', userSelect: 'none', touchAction: 'none' },
  hint: { position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', fontSize: 11, color: '#333333', pointerEvents: 'none', whiteSpace: 'nowrap' },
  mapSubtitle: { fontSize: 12, color: '#555555', padding: '10px 28px 0' },
  list: { flex: 1, overflowY: 'auto', padding: '12px 20px 24px' },
  item: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px', borderRadius: 12, border: '1px solid #2a2a2a', marginBottom: 10, gap: 16, cursor: 'pointer', transition: 'border-color 0.15s, opacity 0.2s, transform 0.2s' },
  itemLeft: { flex: 1, minWidth: 0 },
  itemTitle: { fontSize: 15, fontWeight: 500, color: '#ffffff', marginBottom: 4 },
  itemMeta: { fontSize: 12, color: '#555555', marginBottom: 8 },
  itemBurrows: { display: 'flex', flexWrap: 'wrap', gap: 5 },
  burrowPill: { fontSize: 11, color: '#00b359', background: 'rgba(0,179,89,0.08)', border: '1px solid rgba(0,179,89,0.2)', padding: '2px 8px', borderRadius: 20 },
  burrowMore: { fontSize: 11, color: '#444444', padding: '2px 8px' },
  switchBtn: { fontSize: 12, color: '#555555', whiteSpace: 'nowrap', alignSelf: 'center' },
  itemActions: { display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 },
  restoreBtn: { fontSize: 12, padding: '6px 14px', borderRadius: 8, border: '1px solid #2a2a2a', background: 'transparent', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s', whiteSpace: 'nowrap' },
  deleteBtn: { fontSize: 12, padding: '6px 14px', borderRadius: 8, border: '1px solid #2a2a2a', background: 'transparent', color: '#555555', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s', whiteSpace: 'nowrap' },
  empty: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#444444', fontStyle: 'italic', padding: '48px 28px', textAlign: 'center' },
};

// ── Home Screen ───────────────────────────────────────────────────────────────
function HomeScreen({ onAsk, allBurrows }) {
  return (
    <div style={HS.root}>
      <div style={HS.inner}>
        <h1 style={HS.logo}>Rabbit<span style={HS.dim}>Hole</span></h1>
        <p style={HS.where}>Where to now?</p>
      </div>
    </div>
  );
}

const HS = {
  root: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '60px 40px 0', animation: 'fadeUp 0.4s ease' },
  inner: { maxWidth: 640, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 36 },
  logo: { fontSize: 56, fontWeight: 400, fontFamily: "'Playfair Display', serif", letterSpacing: '-2px', color: '#ffffff', margin: 0 },
  dim: { color: '#00b359' },
  where: { fontSize: 17, color: '#666666', letterSpacing: '0.3px', fontWeight: 300, margin: 0 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' },
  chip: { fontSize: 14, padding: '11px 22px', borderRadius: 100, border: '0.5px solid rgba(255,255,255,0.3)', background: 'transparent', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s' },
};

// ── Article ───────────────────────────────────────────────────────────────────
function ArticlePage({ burrow, onLink, streaming, isDigging, liveText, visitedTopics, onDigDeeper }) {
  // DOM writes handled directly via getElementById in navigate

  const sources = burrow.sources || [];

  if (streaming && !isDigging) {
    return (
      <div style={AP.root}>
        <h1 style={AP.title}>"{burrow.topic}"</h1>
        <p id="stream-body" style={{ ...AP.body, color: '#ffffff', whiteSpace: 'pre-wrap', minHeight: 40 }} />
      </div>
    );
  }
  // Split body into sections (each dig deeper adds a new section)
  const sections = (burrow.body || '').split(/\n\n+/);
  const firstSection = sections[0] || '';
  const deeperSections = sections.slice(1);

  const renderParts = (text) => {
    const p = parseBody(text);
    return p.map((part, i) =>
      part.type === 'text'
        ? <span key={i}>{part.value}</span>
        : (() => {
            const visited = visitedTopics.has(part.topic.toLowerCase().trim());
            return (
              <span key={i} style={visited ? AP.linkVisited : AP.link}
                onClick={() => onLink(part.topic)}
                onMouseEnter={e => { e.currentTarget.style.color = '#00b359'; }}
                onMouseLeave={e => { e.currentTarget.style.color = visited ? '#00b359' : '#00ff88'; }}
              >{part.label}</span>
            );
          })()
    );
  };

  return (
    <div style={AP.root}>
      <h1 style={AP.title}>"{burrow.topic}"</h1>

      {/* First section */}
      <p style={AP.body}>{renderParts(firstSection)}</p>

      {/* Sources after first section */}
      {sources.length > 0 && (
        <div style={AP.sources}>
          <div style={AP.sourcesTitle}>Sources</div>
          {sources.map((s, i) => {
            const sourceTopic = s.title;
            const visited = visitedTopics.has(sourceTopic.toLowerCase().trim());
            return (
              <div key={i} style={AP.sourceItem}>
                <span
                  style={{ ...AP.sourceLink, color: visited ? '#00b359' : '#00ff88' }}
                  onClick={() => onLink(sourceTopic)}
                  onMouseEnter={e => { e.currentTarget.style.color = '#00b359'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = visited ? '#00b359' : '#00ff88'; }}
                >
                  {s.title}
                </span>
                <a href={s.url} target="_blank" rel="noopener noreferrer" style={AP.sourceUrl}
                  onClick={e => e.stopPropagation()}
                >↗</a>
              </div>
            );
          })}
        </div>
      )}

      {/* Deeper sections */}
      {deeperSections.map((sec, i) => (
        <div key={i} style={{ marginTop: 32, paddingTop: 32, borderTop: '1px solid #222222' }}>
          <p style={AP.body}>{renderParts(sec)}</p>
        </div>
      ))}

      {/* Streaming new dig deeper content */}
      {isDigging && (
        <div style={{ marginTop: 32, paddingTop: 32, borderTop: '1px solid #222222' }}>
          <p id="stream-body" style={{ ...AP.body, whiteSpace: 'pre-wrap', minHeight: 40 }} />
        </div>
      )}

      {!streaming && (
        <div style={AP.digWrap}>
          <span style={AP.digBtn}
            onClick={onDigDeeper}
            onMouseEnter={e => { e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.borderColor = '#ffffff'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.borderColor = '#00b359'; }}
          >dig deeper ↓</span>
        </div>
      )}
    </div>
  );
}

const AP = {
  root: { padding: '72px 52px 48px', maxWidth: 780, margin: '0 auto', animation: 'fadeUp 0.35s ease', userSelect: 'text', WebkitUserSelect: 'text' },
  burrowTag: { fontSize: 10, fontWeight: 700, letterSpacing: 2.5, color: '#555555', marginBottom: 18, fontFamily: 'Inter, sans-serif' },
  title: { fontSize: 38, fontWeight: 400, fontFamily: "'Playfair Display', serif", color: '#ffffff', marginBottom: 14, lineHeight: 1.15, letterSpacing: '-0.5px' },
  meta: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 32, fontSize: 13, color: '#555555' },
  label: { color: '#666666' },
  dot: { color: '#444444' },
  domain: { color: '#555555', fontStyle: 'italic' },
  body: { fontSize: 18, lineHeight: 2, color: '#ffffff', userSelect: 'text' },
  link: { color: '#00ff88', textDecoration: 'none', cursor: 'pointer', transition: 'all 0.12s', userSelect: 'none', WebkitUserSelect: 'none' },
  linkVisited: { color: '#00b359', textDecoration: 'none', cursor: 'pointer', transition: 'all 0.12s', userSelect: 'none', WebkitUserSelect: 'none' },
  cursor: { color: '#555555', animation: 'pulse 1s ease-in-out infinite' },
  sources: { marginTop: 36, paddingTop: 24, borderTop: '1px solid #2a2a2a' },
  sourcesTitle: { fontSize: 11, fontWeight: 700, letterSpacing: 2, color: '#444444', marginBottom: 12, fontFamily: 'Inter, sans-serif' },
  sourceItem: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  sourceLink: { fontSize: 14, cursor: 'pointer', transition: 'color 0.1s', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sourceUrl: { fontSize: 12, color: '#444444', textDecoration: 'none', flexShrink: 0 },
  digWrap: { marginTop: 56, marginBottom: 32, display: 'flex', justifyContent: 'center' },
  digBtn: { fontSize: 13, color: '#ffffff', border: '1px solid #00b359', borderRadius: 20, padding: '10px 32px', cursor: 'pointer', transition: 'color 0.15s, border-color 0.15s', letterSpacing: '0.5px', userSelect: 'none', fontFamily: 'Inter, sans-serif' },
};

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [holes, setHoles] = useState(() => {
    const saved = loadState();
    // Always start on the home screen, not mid-burrow
    if (saved && saved.length) return saved.map(h => ({ ...h, currentId: null }));
    return [newHole()];
  });
  const [activeId, setActiveId] = useState(() => {
    const saved = loadState();
    return saved?.[0]?.id || holes[0]?.id;
  });
  const [archive, setArchive] = useState(() => loadArchive());
  const [showWarren, setShowWarren] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [isDigging, setIsDigging] = useState(false);
  const [liveText, setLiveText] = useState('');
  const [error, setError] = useState('');
  const [input, setInput] = useState('');
  const liveRef = useRef('');

  useEffect(() => { saveState(holes); }, [holes]);

  const activeHole = holes.find(h => h.id === activeId) || holes[0];
  const currentBurrow = activeHole?.burrows.find(b => b.id === activeHole.currentId) || null;
  const allBurrows = useMemo(() => holes.flatMap(h => h.burrows), [holes]);
  const visitedTopics = useMemo(() => new Set(activeHole.burrows.map(b => b.topic.toLowerCase().trim())), [activeHole.burrows]);
  const canGoBack = !!(activeHole?.currentId);

  const navigate = useCallback(async (topic) => {
    if (streaming) return;
    setError('');

    const cacheKey = topic.toLowerCase().trim();
    const existing = activeHole.burrows.find(b => b.topic.toLowerCase() === cacheKey);
    if (existing) {
      setHoles(hs => hs.map(h => h.id === activeId ? { ...h, currentId: existing.id } : h));
      return;
    }

    const trail = buildTrail(activeHole.burrows, activeHole.currentId);
    const burrowId = uid();
    // If on home screen but hole has burrows, attach to first root burrow to avoid multiple roots
    const parentId = activeHole.currentId || (activeHole.burrows.length > 0 ? activeHole.burrows[0].id : null);
    const burrow = { id: burrowId, topic, body: '', label: null, domain: null, parentId };

    setHoles(hs => hs.map(h => {
      if (h.id !== activeId) return h;
      const newBurrows = [...h.burrows, burrow];
      const title = newBurrows[0]?.topic || 'New Rabbit Hole';
      return { ...h, burrows: newBurrows, currentId: burrowId };
    }));

    liveRef.current = '';
    setStreaming(true);

    streamAsk(
      topic,
      trail,
      (fullText) => {
        // Write directly to DOM element — no React state update
        liveRef.current = fullText;
        const el = document.getElementById('stream-body');
        if (el) el.textContent = fullText + ' ▍';
        // Don't update React state — DOM write is enough
      },
      (finalText, sources) => {
        // Clear DOM stream element immediately to prevent flash of duplicate
        const el = document.getElementById('stream-body');
        if (el) el.textContent = '';
        // Stream done — commit to holes state once
        const body = finalText || liveRef.current;
        setHoles(hs => hs.map(h => ({
          ...h,
          burrows: h.burrows.map(b => b.id === burrowId ? { ...b, body, sources: sources || [] } : b),
        })));
        setStreaming(false);
        liveRef.current = '';

        apiFetch('/api/label', { topic }).then(({ summary, domain }) => {
          setHoles(hs => hs.map(h => ({
            ...h,
            burrows: h.burrows.map(b => b.id === burrowId ? { ...b, label: summary, domain } : b),
          })));
        }).catch(() => {});
      },
      () => {
        setStreaming(false);
        liveRef.current = '';
        setError('Could not connect. Make sure the server is running and your .env file has a valid ANTHROPIC_API_KEY.');
      }
    );
  }, [activeHole, activeId, streaming]);

  const digDeeper = useCallback(async () => {
    if (streaming || !currentBurrow) return;
    setError('');

    const burrowId = currentBurrow.id;
    const existingBody = currentBurrow.body || '';
    const topic = currentBurrow.topic;

    liveRef.current = '';
    setIsDigging(true);
    setStreaming(true);

    // Build a trail that includes the current burrow's full body
    const trail = buildTrail(activeHole.burrows, activeHole.currentId);

    const digPrompt = `__DIG_DEEPER__${existingBody}`;

    streamAsk(
      digPrompt,
      trail,
      (fullText) => {
        liveRef.current = fullText;
        // Append to existing body in DOM
        const el = document.getElementById('stream-body');
        if (el) el.textContent = fullText + ' ▍';
      },
      (finalText, sources) => {
        const el = document.getElementById('stream-body');
        if (el) el.textContent = '';
        // Append new content to existing body
        const newBody = existingBody + '\n\n' + finalText;
        const newSources = sources?.length ? sources : (currentBurrow.sources || []);
        setHoles(hs => hs.map(h => ({
          ...h,
          burrows: h.burrows.map(b => b.id === burrowId ? { ...b, body: newBody, sources: newSources } : b),
        })));
        setStreaming(false);
        setIsDigging(false);
        liveRef.current = '';
      },
      () => {
        setStreaming(false);
        setIsDigging(false);
        liveRef.current = '';
        setError('Could not connect.');
      }
    );
  }, [currentBurrow, activeHole, activeId, streaming]);

  const goBack = useCallback(() => {
    if (!currentBurrow || streaming) return;
    setHoles(hs => hs.map(h => h.id === activeId ? { ...h, currentId: currentBurrow.parentId || null } : h));
  }, [currentBurrow, activeId, streaming]);

  const jumpTo = useCallback((burrowId) => {
    setHoles(hs => hs.map(h => h.id === activeId ? { ...h, currentId: burrowId } : h));
  }, [activeId]);

  const newHoleHandler = () => {
    const h = newHole();
    setHoles(hs => [...hs, h]);
    setActiveId(h.id);
  };

  const renameHole = (id, newTitle) => {
    const t = newTitle.trim();
    if (!t) return;
    setHoles(hs => hs.map(h => h.id === id ? { ...h, title: t } : h));
  };

  const reorderHoles = (fromIndex, toIndex) => {
    setHoles(hs => {
      const next = [...hs];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const closeHole = (id) => {
    setHoles(hs => {
      const hole = hs.find(h => h.id === id);
      if (hole) {
        const archived = { ...hole, deletedAt: Date.now() };
        setArchive(a => { const next = [...a, archived]; saveArchive(next); return next; });
      }
      const next = hs.filter(h => h.id !== id);
      if (next.length === 0) {
        const fresh = newHole();
        setActiveId(fresh.id);
        return [fresh];
      }
      if (activeId === id) setActiveId(next[next.length - 1]?.id);
      return next;
    });
  };

  const restoreHole = (hole) => {
    const restored = { ...hole, deletedAt: undefined };
    setHoles(hs => [...hs, restored]);
    // Don't switch — just restore quietly to "Your Rabbit Holes"
    setArchive(a => { const next = a.filter(h => h.id !== hole.id); saveArchive(next); return next; });
  };

  const deletePermanent = (id) => {
    setArchive(a => { const next = a.filter(h => h.id !== id); saveArchive(next); return next; });
  };

  const submit = () => {
    const q = input.trim();
    if (q && !streaming) { setInput(''); navigate(q); }
  };

  return (
    <div style={A.root}>
      <header style={A.header}>
        <button style={{ ...A.mapBtn, position: 'fixed', top: 16, left: 20, zIndex: 100 }} onClick={() => setShowWarren(true)}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#ffffff'; e.currentTarget.style.color = '#ffffff'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888888'; }}>
          ⬡ Map
          {activeHole.burrows.length > 0 && <span style={A.warrenCount}>{activeHole.burrows.length}</span>}
        </button>
        <div style={{ flex: 1 }} />
        <button style={{ ...A.menuBtn, position: 'fixed', top: 16, right: 20, zIndex: 100 }} onClick={() => setShowMenu(m => !m)}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#ffffff'; e.currentTarget.style.color = '#ffffff'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888888'; }}>
          ☰ Menu
        </button>
      </header>

      <main style={A.main}>
        {error && <div style={A.error}>{error}</div>}
        {currentBurrow
          ? <ArticlePage burrow={currentBurrow} onLink={navigate} streaming={streaming} isDigging={isDigging} liveText={liveText} visitedTopics={visitedTopics} onDigDeeper={digDeeper} />
          : <HomeScreen onAsk={navigate} allBurrows={allBurrows} />
        }
      </main>

      {/* Back (left) + New hole (right) row */}
      <div style={A.mapBar}>
        {canGoBack && !streaming && (() => {
          const byId = {};
          activeHole.burrows.forEach(b => { byId[b.id] = b; });
          const parentBurrow = currentBurrow?.parentId ? byId[currentBurrow.parentId] : null;
          return (
            <button style={{ ...A.backBtn, position: 'fixed', bottom: 90, left: 20, zIndex: 100 }} onClick={goBack}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = '#ffffff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#aaaaaa'; }}
            >← Back</button>
          );
        })()}
        <div style={{ flex: 1 }} />
      </div>

      {/* Search bar — input and shovel fused */}
      <div style={A.inputBar}>
        <input
          style={{ ...A.input, opacity: streaming ? 0.5 : 1, borderRadius: '12px 0 0 12px', borderRight: 'none' }}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder={streaming ? 'Digging…' : 'Ask anything...'}
          disabled={streaming}
          autoComplete="off"
        />
        <button style={{ ...A.askBtn, opacity: streaming ? 0.4 : 1, borderRadius: '0 12px 12px 0', background: '#161616', border: '1px solid #2a2a2a', borderLeft: 'none' }} onClick={submit} disabled={streaming}>
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M17 4l3 3" />
            <path d="M18.5 5.5l-8 8" />
            <path d="M8.276 11.284l4.44 4.44a.968 .968 0 0 1 0 1.369l-2.704 2.704a4.108 4.108 0 0 1 -5.809 -5.81l2.704 -2.703a.968 .968 0 0 1 1.37 0l-.001 0" />
          </svg>
        </button>
      </div>
      {streaming && <div style={A.streamingBadge}>digging…</div>}

      {showMenu && <MenuPanel holes={holes} activeHole={activeHole} archive={archive} onSwitch={id => setActiveId(id)} onCloseHole={closeHole} onRestore={restoreHole} onDeletePermanent={deletePermanent} onViewWarren={(id) => { setActiveId(id); setShowMenu(false); setTimeout(() => setShowWarren(true), 50); }} onClose={() => setShowMenu(false)} onRename={renameHole} onNewHole={newHoleHandler} />}
      {showWarren && <WarrenPanel activeHole={activeHole} holes={holes} archive={archive} onJump={jumpTo} onSwitch={id => { setActiveId(id); }} onRestore={restoreHole} onDeletePermanent={deletePermanent} onClose={() => setShowWarren(false)} onViewWarren={(id) => { setActiveId(id); setTimeout(() => setShowWarren(true), 50); }} onRenameHole={renameHole} onCloseHole={closeHole} />}
    </div>
  );
}

const A = {
  root: { display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden', background: '#1a1a1a', fontFamily: 'Inter, sans-serif', userSelect: 'none', WebkitUserSelect: 'none' },
  header: { height: 0, flexShrink: 0 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 14, minWidth: 0, flex: 1, overflow: 'hidden' },
  backBtn: { fontSize: 17, padding: '10px 22px', borderRadius: 12, border: '1px solid #2a2a2a', background: '#1a1a1a', color: '#aaaaaa', cursor: 'pointer', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.15s' },
  crumb: { fontSize: 13, color: '#555555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  backLabel: { fontSize: 13, color: '#666666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  streamingBadge: { fontSize: 12, color: '#555555', fontStyle: 'italic', textAlign: 'center', padding: '0 20px 8px' },
  warrenBtn: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '7px 16px', borderRadius: 8, border: '1px solid #2a2a2a', background: 'transparent', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', flexShrink: 0 },
  menuBtn: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 17, padding: '10px 22px', borderRadius: 12, border: '1px solid #2a2a2a', background: '#1a1a1a', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.15s' },
  mapBar: { height: 0 },
  mapBtn: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 17, padding: '10px 22px', borderRadius: 12, border: '1px solid #2a2a2a', background: '#1a1a1a', color: '#888888', cursor: 'pointer', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.15s' },
  newHoleBtn: { fontSize: 24, width: 46, height: 46, borderRadius: 12, border: '1px solid #2a2a2a', background: '#1a1a1a', color: '#888888', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' },
  warrenCount: { fontSize: 11, background: '#222222', color: '#666666', padding: '2px 7px', borderRadius: 10 },
  main: { flex: 1, overflowY: 'auto', position: 'relative' },
  error: { margin: '20px 32px', padding: '14px 18px', borderRadius: 10, background: '#2b1f1f', border: '1px solid #6b2020', fontSize: 14, color: '#7eb8c9', lineHeight: 1.6 },
  inputBar: { display: 'flex', gap: 0, padding: '4px 20px 16px', background: 'transparent', flexShrink: 0 },
  input: { flex: 1, background: '#161616', border: '1px solid #2a2a2a', borderRadius: 12, padding: '13px 20px', fontSize: 16, color: '#f0f0f0', outline: 'none', fontFamily: 'Inter, sans-serif', userSelect: 'text', WebkitUserSelect: 'text', transition: 'opacity 0.2s' },
  askBtn: { width: 48, height: 48, borderRadius: 12, border: 'none', background: '#222222', color: '#888888', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
};

